import React from "react";
import { withRouter } from "react-router-dom";
import Loader from "../../../../components/Loader";
import { getMessage } from "../../../../lib/translator";
import {
  BaseForm,
  Input,
  Checkbox,
  CheckboxGroup,
  VALIDATION_TYPES,
} from "../../../../components/Form";
import API from "../../../../lib/api";
import { Dialog } from "../../../../components/Popup";
import "./style.css";
import { GO_HOST } from "../../../../config/app";
import DeleteIcon from "./DeleteIcon.jsx";
import { getExtensionDetails, isExtensionEnabled } from "../../../../lib/auth";

const COD = "COD";
const PICKUP = "PICKUP";
const DELIVERY = "DELIVERY";

const showConfig = () => {
  if (GO_HOST.includes("tamimimarkets.com")) return false;
  return true;
};

class OrderManagementForm extends BaseForm {
  constructor(props) {
    super(props);
    this.state.showLoader = false;
    this.handleClose = this.handleClose.bind(this);
    this.closeDialogs = this.closeDialogs.bind(this);
    this.state.hasConfigAccess = showConfig();
    this.state.showPaymentError = false;
    this.state.adminReasons = [];
    this.state.consumerReasons = [];
    this.state.isMultiLingualSupportEnabled = false;
  }
  onPaymentChange = (value, type, key) => {
    if (type === "PICKUP") {
      const paymentMethods = this.state.values.configuredPaymentsPickup || [];
      if (value) {
        paymentMethods.push(key);
      } else {
        const index = paymentMethods.indexOf(key);
        paymentMethods.splice(index, 1);
      }

      this.setState((prevState) => {
        return {
          values: {
            ...prevState.values,
            configuredPaymentsPickup: paymentMethods,
          },
        };
      });
    } else {
      const paymentMethods = this.state.values.configuredPaymentsDelivery || [];
      if (value) {
        paymentMethods.push(key);
      } else {
        const index = paymentMethods.indexOf(key);
        paymentMethods.splice(index, 1);
      }
      this.setState((prevState) => {
        return {
          values: {
            ...prevState.values,
            configuredPaymentsDelivery: paymentMethods,
          },
        };
      });
    }
  };
  handleReasonChange = (index, type, value, lang = "en") => {
    const stateKey = type === "admin" ? "adminReasons" : "consumerReasons";
    this.setState((prevState) => {
      const reasons = [...prevState[stateKey]];
      const existing = reasons[index] || { en: "", ar: "" };
      reasons[index] = { ...existing, [lang]: value };

      return {
        [stateKey]: reasons,
        values: {
          ...prevState.values,
          [`${type}-reason-${lang}-${index}`]: value,
        },
      };
    });
  };

  addReason = (type) => {
    const stateKey = type === "admin" ? "adminReasons" : "consumerReasons";
    this.setState((prevState) => {
      const reasons = [...prevState[stateKey]];
      const customLength = reasons.filter((r) => !r?.isOther).length;
      if (customLength >= 5) {
        return prevState;
      }
      const newReason = { en: "", ar: "" };
      let insertIndex = reasons.length;
      const othersIndex = reasons.findIndex((r) => r?.isOther);
      if (othersIndex !== -1) {
        insertIndex = othersIndex;
      }
      reasons.splice(insertIndex, 0, newReason);

      const newValues = { ...prevState.values };
      newValues[`${type}-reason-en-${insertIndex}`] = "";
      if (prevState.isMultiLingualSupportEnabled) {
        newValues[`${type}-reason-ar-${insertIndex}`] = "";
      }

      for (let i = insertIndex + 1; i < reasons.length; i++) {
        newValues[`${type}-reason-en-${i}`] = reasons[i].en || "";
        if (prevState.isMultiLingualSupportEnabled) {
          newValues[`${type}-reason-ar-${i}`] = reasons[i].ar || "";
        }
      }

      return {
        [stateKey]: reasons,
        values: newValues,
      };
    });
  };

  removeReason = (index, type) => {
    const stateKey = type === "admin" ? "adminReasons" : "consumerReasons";
    this.setState((prevState) => {
      const reasons = [...prevState[stateKey]];
      reasons.splice(index, 1);

      const newValues = { ...prevState.values };
      delete newValues[`${type}-reason-en-${index}`];
      delete newValues[`${type}-reason-ar-${index}`];

      for (let i = index; i < reasons.length; i++) {
        newValues[`${type}-reason-en-${i}`] = reasons[i].en || "";
        if (prevState.isMultiLingualSupportEnabled) {
          newValues[`${type}-reason-ar-${i}`] = reasons[i].ar || "";
        }
      }

      delete newValues[`${type}-reason-en-${reasons.length}`];
      delete newValues[`${type}-reason-ar-${reasons.length}`];

      return {
        [stateKey]: reasons,
        values: newValues,
      };
    });
  };

  componentDidMount() {
    this.setState({
      showLoader: true,
    });

    if (isExtensionEnabled("MultiLingualSupport")) {
      const extension = getExtensionDetails("MultiLingualSupport");
      const id = extension && extension.id;
      if (id) {
        const api = new API({ url: `/account-service/extension/${id}` });
        api
          .get()
          .then((response) => {
            const languages =
              response?.data?.extension?.config?.globalConfig?.languages || [];
            const isArabicEnabled =
              Array.isArray(languages) && languages.includes("ar");
            this.setState({
              isMultiLingualSupportEnabled: isArabicEnabled,
            });
          })
          .catch((error) => {
            console.error(error);
          });
      }
    }

    const logisticApi = new API({ url: `/config-service/config/logistics` });
    const orderApi = new API({ url: `/account-service/config/order` });
    const orderConfigApi = new API({
      url: "/config-service/config/order",
    });
    Promise.all([
      orderApi.get(),
      logisticApi.get(),
      orderConfigApi.get({}, { removeLanguageHeader: true }),
    ])
      .then(([orderApiResponse, logisticApiResponse, orderConfigResponse]) => {
        let values = JSON.parse(JSON.stringify(this.state.values));
        const orderPayments = orderConfigResponse.data.order;
        const paymentMethods = [
          "ONLINE",
          "WALLET",
          "CREDIT-AMEX",
          "CREDIT-VISA",
          "CREDIT-MASTER",
          "DEBIT-MADA",
          "GIFTCARD",
          "APPLE_PAY",
          "COD",
        ];

        values.configuredPaymentsPickup =
          orderPayments?.paymentMethods?.PICKUP || [];
        values.configuredPaymentsDelivery =
          orderPayments?.paymentMethods?.DELIVERY || [];

        values.minimumOrderValue =
          (orderApiResponse &&
            orderApiResponse.data &&
            orderApiResponse.data.config &&
            orderApiResponse.data.config.order &&
            orderApiResponse.data.config.order.minimumOrderValue) ||
          0;
        values.deliveryFee =
          (logisticApiResponse &&
            logisticApiResponse.data &&
            logisticApiResponse.data.logistics &&
            logisticApiResponse.data.logistics.deliveryFee) ||
          0;
        // values.paymentMethods =
        //   orderConfigResponse &&
        //   orderConfigResponse.data &&
        //   orderConfigResponse.data.order &&
        //   orderConfigResponse.data.order.paymentMethods;

        // values.isCODEnabled =
        //   orderConfigResponse &&
        //   orderConfigResponse.data &&
        //   orderConfigResponse.data.order &&
        //   orderConfigResponse.data.order.paymentMethods &&
        //   orderConfigResponse.data.order.paymentMethods?.includes(COD);
        values.generateRandomReferenceNumbers = Boolean(
          orderConfigResponse &&
            orderConfigResponse.data &&
            orderConfigResponse.data.order &&
            orderConfigResponse.data.order.generateRandomReferenceNumbers
        );
        values.orderTypes = orderApiResponse?.data?.config?.order?.orderTypes;
        values.minimumOrderValueForFreeDelivery =
          orderConfigResponse?.data?.order?.minimumOrderValueForFreeDelivery;
        values.minimumOrderValueForFreePickup =
          orderConfigResponse?.data?.order?.minimumOrderValueForFreePickUp;
        values.minimumOrderValueForDelivery =
          orderConfigResponse?.data?.order?.minimumOrderValueForDelivery;
        values.minimumOrderValueForPickup =
          orderConfigResponse?.data?.order?.minimumOrderValueForPickup;
        values.enableCancellationReasons = Boolean(
          orderConfigResponse?.data?.order?.cancellationReasons
        );
        const cancellationReasons =
          orderConfigResponse?.data?.order?.cancellationReasons;
        let adminReasons = [];
        let consumerReasons = [];

        if (cancellationReasons) {
          const parseReason = (reasonStr) => {
            if (reasonStr?.includes("::")) {
              const [enPart, arPart] = reasonStr
                .split("::")
                .map((s) => s.trim());

              let enText = enPart.replace(/^en-/, "").trim();
              let arText = arPart.replace(/^ar-/, "").trim();

              const isOther =
                enText.startsWith("Other:") ||
                enText === "Others" ||
                reasonStr.includes("Other:");

              if (isOther) {
                return {
                  en: enText.replace("Other:", "").trim() || "Others",
                  ar: arText.replace("Other:", "").trim() || "",
                  isOther: true,
                };
              }

              return { en: enText, ar: arText, isOther: false };
            }

            if (reasonStr.startsWith("Other:")) {
              return {
                en: reasonStr.replace("Other:", "").trim() || "Others",
                ar: "",
                isOther: true,
              };
            }

            if (reasonStr === "Others") {
              return { en: "Others", ar: "", isOther: true };
            }

            return { en: reasonStr, ar: "", isOther: false };
          };

          adminReasons = cancellationReasons.admin
            ? cancellationReasons.admin
                .filter((r) => r !== "Others")
                .map(parseReason)
            : [];
          consumerReasons = cancellationReasons.consumer
            ? cancellationReasons.consumer
                .filter((r) => r !== "Others")
                .map(parseReason)
            : [];
          const ensureOthersLast = (list, enableArabic) => {
            let othersItem = list.find((r) => r?.isOther);
            if (!othersItem) {
              othersItem = {
                en: "Others",
                ar: enableArabic ? "أخرى" : "",
                isOther: true,
              };
            }
            const filtered = list.filter((r) => !r?.isOther);
            return [...filtered, othersItem];
          };
          adminReasons = ensureOthersLast(
            adminReasons,
            this.state.isMultiLingualSupportEnabled
          );
          consumerReasons = ensureOthersLast(
            consumerReasons,
            this.state.isMultiLingualSupportEnabled
          );
        }

        const reasonValues = {};
        adminReasons.forEach((reason, index) => {
          reasonValues[`admin-reason-en-${index}`] = reason.en || "";
          if (this.state.isMultiLingualSupportEnabled) {
            reasonValues[`admin-reason-ar-${index}`] = reason.ar || "";
          }
        });
        consumerReasons.forEach((reason, index) => {
          reasonValues[`consumer-reason-en-${index}`] = reason.en || "";
          if (this.state.isMultiLingualSupportEnabled) {
            reasonValues[`consumer-reason-ar-${index}`] = reason.ar || "";
          }
        });

        this.setState({
          values: {
            ...values,
            ...reasonValues,
          },
          showLoader: false,
          availablePayments: paymentMethods,
          adminReasons,
          consumerReasons,
        });
      })
      .catch((error) => {
        console.error(error);
        this.setState({ showLoader: false });
        if (error.code === 401) {
          throw error;
        }
      });
  }
  handleSubmit(data = {}) {
    const { configuredPaymentsPickup, configuredPaymentsDelivery } =
      this.state.values;
    let payload = {};
    if (this.state.values?.orderTypes?.includes(PICKUP)) {
      payload[PICKUP] = configuredPaymentsPickup;
    }
    if (this.state.values?.orderTypes?.includes(DELIVERY)) {
      payload[DELIVERY] = configuredPaymentsDelivery;
    }

    let order = {};
    if (data && data.hasOwnProperty("minimumOrderValue")) {
      order.minimumOrderValue = data["minimumOrderValue"];
    } else {
      order.minimumOrderValue = 0;
    }

    if (data.orderTypes) {
      order.orderTypes = data.orderTypes;
    }

    const logistics = {},
      orderConfig = {};
    logistics.deliveryFee = data["deliveryFee"];
    orderConfig.generateRandomReferenceNumbers = Boolean(
      data["generateRandomReferenceNumbers"]
    );
    orderConfig.minimumOrderValueForDelivery =
      data.minimumOrderValueForDelivery;
    orderConfig.minimumOrderValueForPickup = data.minimumOrderValueForPickup;
    orderConfig.minimumOrderValueForFreeDelivery =
      data.minimumOrderValueForFreeDelivery;
    if (data.enableCancellationReasons) {
      const isArabic = this.state.isMultiLingualSupportEnabled || false;
      const defaultAdminEn = ["Duplicate Order", "Item Short", "Order Delay"];
      const defaultAdminAr = ["طلب مكرر", "نقص في العنصر", "تأخير في الطلب"];
      const defaultConsumerEn = [
        "Change My Mind",
        "Delivery Delays",
        "Product Unavailability",
      ];
      const defaultConsumerAr = [
        "غير رأيي",
        "تأخيرات في التوصيل",
        "عدم توفر المنتج",
      ];

      const buildReasonsFromData = (type) => {
        const reasons = [];
        const { adminReasons, consumerReasons } = this.state;
        const sourceReasons = type === "admin" ? adminReasons : consumerReasons;

        sourceReasons.forEach((reason, index) => {
          const enValue = data[`${type}-reason-en-${index}`]?.trim() || "";
          const arValue = data[`${type}-reason-ar-${index}`]?.trim() || "";

          if (reason.isOther) {
            reasons.push({
              en: enValue || reason.en,
              ar: arValue || reason.ar,
              isOther: true,
            });
          } else {
            if (enValue || arValue) {
              reasons.push({
                en: enValue,
                ar: arValue,
                isOther: false,
              });
            }
          }
        });

        return reasons;
      };

      const adminReasonsFromData = buildReasonsFromData("admin");
      const consumerReasonsFromData = buildReasonsFromData("consumer");

      let finalAdminReasons = [];
      let finalConsumerReasons = [];

      const getOthersText = (reasons) => {
        const others = reasons.find((r) => r.isOther);
        return {
          en: others?.en?.trim() || "Others",
          ar: others?.ar?.trim() || "",
        };
      };

      if (isArabic) {
        const formatReason = (r) => {
          const enText = (r.en || "").trim();
          const arText = (r.ar || "").trim();

          const formattedEn = r.isOther
            ? `Other:${enText || "Others"}`
            : enText;

          const formattedAr = r.isOther
            ? `Other:${arText || "Others"}`
            : arText;

          if (!formattedAr) {
            return `en-${formattedEn} ::`;
          }

          return `en-${formattedEn} :: ar-${formattedAr}`;
        };

        const customAdmin = adminReasonsFromData.filter(
          (r) => !r.isOther && (r.en?.trim() !== "" || r.ar?.trim() !== "")
        );
        if (customAdmin.length > 0) {
          const formattedCustomAdmin = customAdmin.map(formatReason);
          const othersAdmin = adminReasonsFromData.find((r) => r.isOther) || {
            en: "Others",
            ar: "أخرى",
            isOther: true,
          };
          const formattedOthersAdmin = formatReason(othersAdmin);
          finalAdminReasons = [...formattedCustomAdmin, formattedOthersAdmin];
        } else {
          const othersText = getOthersText(adminReasonsFromData);
          finalAdminReasons = defaultAdminEn
            .map((en, i) => `en-${en} :: ar-${defaultAdminAr[i]}`)
            .concat(
              `en-Other:${othersText.en} :: ar-Other:${othersText.ar || "أخرى"}`
            );
        }

        const customConsumer = consumerReasonsFromData.filter(
          (r) => !r.isOther && (r.en?.trim() !== "" || r.ar?.trim() !== "")
        );
        if (customConsumer.length > 0) {
          const formattedCustomConsumer = customConsumer.map(formatReason);
          const othersConsumer = consumerReasonsFromData.find(
            (r) => r.isOther
          ) || {
            en: "Others",
            ar: "أخرى",
            isOther: true,
          };
          const formattedOthersConsumer = formatReason(othersConsumer);
          finalConsumerReasons = [
            ...formattedCustomConsumer,
            formattedOthersConsumer,
          ];
        } else {
          const othersText = getOthersText(consumerReasonsFromData);
          finalConsumerReasons = defaultConsumerEn
            .map((en, i) => `en-${en} :: ar-${defaultConsumerAr[i]}`)
            .concat(
              `en-Other:${othersText.en} :: ar-Other:${othersText.ar || "أخرى"}`
            );
        }
      } else {
        const formatPlain = (enText) => `en-${enText} ::`;

        const customAdmin = adminReasonsFromData.filter(
          (r) => !r.isOther && r.en?.trim() !== ""
        );
        const othersAdminText = getOthersText(adminReasonsFromData).en;
        const formattedOthersAdmin = formatPlain(`Other:${othersAdminText}`);
        if (customAdmin.length > 0) {
          const formattedCustomAdmin = customAdmin.map((r) =>
            formatPlain(r.en.trim())
          );
          finalAdminReasons = [...formattedCustomAdmin, formattedOthersAdmin];
        } else {
          finalAdminReasons = defaultAdminEn
            .map((en) => formatPlain(en))
            .concat(formattedOthersAdmin);
        }

        const customConsumer = consumerReasonsFromData.filter(
          (r) => !r.isOther && r.en?.trim() !== ""
        );
        const othersConsumerText = getOthersText(consumerReasonsFromData).en;
        const formattedOthersConsumer = formatPlain(
          `Other:${othersConsumerText}`
        );
        if (customConsumer.length > 0) {
          const formattedCustomConsumer = customConsumer.map((r) =>
            formatPlain(r.en.trim())
          );
          finalConsumerReasons = [
            ...formattedCustomConsumer,
            formattedOthersConsumer,
          ];
        } else {
          finalConsumerReasons = defaultConsumerEn
            .map((en) => formatPlain(en))
            .concat(formattedOthersConsumer);
        }
      }

      orderConfig.cancellationReasons = {
        admin: finalAdminReasons,
        consumer: finalConsumerReasons,
        isArabicDynamic: this.state.isMultiLingualSupportEnabled || false,
      };
    } else {
      orderConfig.cancellationReasons = null;
    }

    if (data?.minimumOrderValueForFreePickup) {
      orderConfig.minimumOrderValueForFreePickUp =
        data.minimumOrderValueForFreePickup;
    }
    if (data.isCODEnabled) {
      if (data.paymentMethods.indexOf(COD) > -1) {
        // Removing duplicate entries. This is for cleaning already affected data
        data.paymentMethods = data.paymentMethods.filter(
          (paymentMeyhod, index, array) =>
            array.indexOf(paymentMeyhod) === index
        );
      } else {
        data.paymentMethods = [...data.paymentMethods, COD];
      }
      orderConfig.paymentMethods = payload;
    } else {
      orderConfig.paymentMethods = payload;
    }
    // orderConfig.orderTypesToPaymentMethods = payload;

    const logisticAndOrderConfigApi = new API({
      url: `/config-service/config`,
    });
    const api = new API({ url: `/account-service/config/order` });

    return Promise.all([
      api.put({ order }),
      logisticAndOrderConfigApi.post({ logistics, order: orderConfig }),
    ])
      .then(([orderResponse, logisticApiResponse]) => {
        this.setState({ formError: "", showSuccessDialog: true });
      })
      .catch((error) => {
        console.error(error);
        this.setState({ formError: error.message, showErrorDialog: true });

        if (error.code === 401) {
          throw error;
        }
      });
  }

  validatePaymentModes = (data) => {
    const hasPickup = data.orderTypes?.includes(PICKUP);
    const hasDelivery = data.orderTypes?.includes(DELIVERY);

    switch (true) {
      case hasPickup && hasDelivery:
        return (
          data.configuredPaymentsPickup.length > 0 &&
          data.configuredPaymentsDelivery.length > 0
        );
      case hasPickup:
        return data.configuredPaymentsPickup.length > 0;
      case hasDelivery:
        return data.configuredPaymentsDelivery.length > 0;
      default:
        return false;
    }
  };

  onSubmit(data) {
    if (this.validatePaymentModes(data)) {
      this.setState({
        submitting: true,
        showPaymentError: false,
      });
      this.handleSubmit(data).then(() => {
        this.setState({ submitting: false });
      });
    } else {
      this.setState({
        showPaymentError: true,
      });
    }
  }
  handleClose() {
    this.props.history.goBack();
  }
  closeDialogs() {
    this.setState({
      showSuccessDialog: false,
      showErrorDialog: false,
      showPaymentError: false,
    });
  }
  render() {
    const { SubmitButton } = this.buttons;
    const { Form } = this.components;
    const {
      hasConfigAccess,
      availablePayments = [],
      adminReasons,
      consumerReasons,
      isMultiLingualSupportEnabled,
    } = this.state;
    const customAdminCount = adminReasons.filter((r) => !r?.isOther).length;
    const customConsumerCount = consumerReasons.filter(
      (r) => !r?.isOther
    ).length;
    const isArabicEnabled = isMultiLingualSupportEnabled;
    return (
      <div>
        <Dialog
          show={this.state.showSuccessDialog}
          className="success"
          information={getMessage("basicInfo.save.success.title")}
          close={this.closeDialogs}
          closeText={getMessage("basicInfo.save.success.closeText")}
        />
        <Dialog
          show={this.state.showErrorDialog}
          information={getMessage("basicInfo.save.error.title")}
          close={this.closeDialogs}
          closeText={getMessage("basicInfo.save.error.closeText")}
        />
        <Dialog
          show={this.state.showPaymentError}
          information={getMessage("orderConfig.form.payment.validate.error")}
          close={this.closeDialogs}
          closeText={getMessage("notification.okay.text")}
        />
        {this.state.showLoader ? (
          <Loader />
        ) : (
          <Form className="order-management-form">
            {hasConfigAccess && (
              <Input
                label={getMessage("orderConfig.form.minimumOrderValue.heading")}
                placeholder={getMessage(
                  "orderConfig.form.minimumOrderValue.placeholder"
                )}
                name="minimumOrderValue"
                type="number"
                min={0}
                {...this.generateStateMappers({
                  stateKeys: ["minimumOrderValue"],
                  loseEmphasisOnFill: true,
                })}
              />
            )}
            {this.state.values?.orderTypes?.includes(DELIVERY) &&
              hasConfigAccess && (
                <Input
                  label={getMessage(
                    "orderConfig.form.minimumOrderValueFreeDelivery.heading"
                  )}
                  placeholder={getMessage(
                    "orderConfig.form.minimumOrderValueFreeDelivery.placeholder"
                  )}
                  name="minimumOrderValueForFreeDelivery"
                  type="number"
                  min={0}
                  {...this.generateStateMappers({
                    stateKeys: ["minimumOrderValueForFreeDelivery"],
                    loseEmphasisOnFill: true,
                  })}
                />
              )}
            {this.state.values?.orderTypes?.includes(PICKUP) &&
              hasConfigAccess && (
                <Input
                  label={getMessage(
                    "orderConfig.form.minimumOrderValueFreePickup.heading"
                  )}
                  placeholder={getMessage(
                    "orderConfig.form.minimumOrderValueFreePickup.placeholder"
                  )}
                  name="minimumOrderValueForFreePickup"
                  type="number"
                  min={0}
                  {...this.generateStateMappers({
                    stateKeys: ["minimumOrderValueForFreePickup"],
                    loseEmphasisOnFill: true,
                  })}
                />
              )}
            {this.state.values?.orderTypes?.includes(DELIVERY) && (
              <Input
                label={getMessage(
                  "orderConfig.form.minimumOrderValueForDelivery.heading"
                )}
                placeholder={getMessage(
                  "orderConfig.form.minimumOrderValueForDelivery.placeholder"
                )}
                name="minimumOrderValueForDelivery"
                type="number"
                min={0}
                {...this.generateStateMappers({
                  stateKeys: ["minimumOrderValueForDelivery"],
                  loseEmphasisOnFill: true,
                })}
              />
            )}
            {this.state.values?.orderTypes?.includes(PICKUP) && (
              <Input
                label={getMessage(
                  "orderConfig.form.minimumOrderValueForPickup.heading"
                )}
                placeholder={getMessage(
                  "orderConfig.form.minimumOrderValueForPickup.placeholder"
                )}
                name="minimumOrderValueForPickup"
                type="number"
                min={0}
                {...this.generateStateMappers({
                  stateKeys: ["minimumOrderValueForPickup"],
                  loseEmphasisOnFill: true,
                })}
              />
            )}
            <Input
              label={getMessage("orderConfig.form.deliveryFees.heading")}
              placeholder={getMessage(
                "orderConfig.form.deliveryFees.placeholder"
              )}
              name="deliveryFee"
              type="number"
              min={0}
              {...this.generateStateMappers({
                stateKeys: ["deliveryFee"],
                loseEmphasisOnFill: true,
              })}
            />
            {/* {hasConfigAccess && (
              <div className="is-cod-enabled">
                <Checkbox
                  label={getMessage("")}
                  inlineLabel={getMessage("Enable COD")}
                  name={`isCODEnabled`}
                  {...this.generateStateMappers({
                    stateKeys: ["isCODEnabled"],
                    loseEmphasisOnFill: true,
                  })}
                />
              </div>
            )} */}
            {hasConfigAccess && (
              <Checkbox
                label={getMessage("order.config.referenceNumber.heading")}
                inlineLabel={getMessage(
                  "order.config.generateRandomReferenceNumbers"
                )}
                name={`generateRandomReferenceNumbers`}
                className="field reference-number"
                {...this.generateStateMappers({
                  stateKeys: ["generateRandomReferenceNumbers"],
                  loseEmphasisOnFill: true,
                })}
              />
            )}
            <div className="order-cancellation">
              <Checkbox
                label={getMessage("order.config.enableCancellationReasons")}
                inlineLabel={getMessage(
                  "order.config.details.enableCancellationReasons"
                )}
                name={`enableCancellationReasons`}
                {...this.generateStateMappers({
                  stateKeys: ["enableCancellationReasons"],
                  loseEmphasisOnFill: true,
                })}
              />
              <div className="description">
                {getMessage(
                  "order.config.description.enableCancellationReasons"
                )}
              </div>
            </div>

            {this.state.values?.enableCancellationReasons && (
              <div className="cancellation-reasons-container">
                <div
                  className={`cancellation-reasons-flex ${
                    isArabicEnabled ? "arabic-enabled" : ""
                  }`}
                >
                  <div className="reason-section">
                    <div className="form-label">Admin Reasons: </div>
                    {adminReasons.map((reason, index) => {
                      return (
                        <div key={index} className="reason-input-wrapper">
                          <Input
                            name={`admin-reason-en-${index}`}
                            placeholder={`Enter English reason ${index + 1}`}
                            maxLength="30"
                            onChange={(value) =>
                              this.handleReasonChange(
                                index,
                                "admin",
                                value,
                                "en"
                              )
                            }
                            required
                            {...this.generateStateMappers({
                              stateKeys: [`admin-reason-en-${index}`],
                              validationType: VALIDATION_TYPES.ONSUBMIT,
                            })}
                            validationStrings={{
                              valueMissing: getMessage("input.requiredMessage"),
                            }}
                          />
                          {isArabicEnabled && (
                            <Input
                              name={`admin-reason-ar-${index}`}
                              placeholder={`Enter Arabic reason ${index + 1}`}
                              maxLength="30"
                              onChange={(value) =>
                                this.handleReasonChange(
                                  index,
                                  "admin",
                                  value,
                                  "ar"
                                )
                              }
                              dir="rtl"
                              required
                              {...this.generateStateMappers({
                                stateKeys: [`admin-reason-ar-${index}`],
                                validationType: VALIDATION_TYPES.ONSUBMIT,
                              })}
                              validationStrings={{
                                valueMissing: getMessage(
                                  "input.requiredMessage"
                                ),
                              }}
                            />
                          )}
                          {!reason?.isOther && (
                            <div
                              className="delete-icon-button"
                              onClick={() => this.removeReason(index, "admin")}
                            >
                              <DeleteIcon
                                color="#343330"
                                width={20}
                                height={20}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {customAdminCount < 5 && (
                      <button
                        type="button"
                        className="add-reason-button"
                        onClick={() => this.addReason("admin")}
                      >
                        {customAdminCount > 0
                          ? "+ Add More Reasons"
                          : "+ Add Reason"}
                      </button>
                    )}
                  </div>
                  <div className="reason-section">
                    <div className="form-label">Consumer Reasons: </div>
                    {consumerReasons.map((reason, index) => {
                      return (
                        <div key={index} className="reason-input-wrapper">
                          <Input
                            name={`consumer-reason-en-${index}`}
                            placeholder={`Enter English reason ${index + 1}`}
                            maxLength="30"
                            onChange={(value) =>
                              this.handleReasonChange(
                                index,
                                "consumer",
                                value,
                                "en"
                              )
                            }
                            required
                            {...this.generateStateMappers({
                              stateKeys: [`consumer-reason-en-${index}`],
                              validationType: VALIDATION_TYPES.ONSUBMIT,
                            })}
                            validationStrings={{
                              valueMissing: getMessage("input.requiredMessage"),
                            }}
                          />
                          {isArabicEnabled && (
                            <Input
                              name={`consumer-reason-ar-${index}`}
                              placeholder={`Enter Arabic reason ${index + 1}`}
                              maxLength="30"
                              onChange={(value) =>
                                this.handleReasonChange(
                                  index,
                                  "consumer",
                                  value,
                                  "ar"
                                )
                              }
                              dir="rtl"
                              required
                              {...this.generateStateMappers({
                                stateKeys: [`consumer-reason-ar-${index}`],
                                validationType: VALIDATION_TYPES.ONSUBMIT,
                              })}
                              validationStrings={{
                                valueMissing: getMessage(
                                  "input.requiredMessage"
                                ),
                              }}
                            />
                          )}
                          {!reason?.isOther && (
                            <div
                              className="delete-icon-button"
                              onClick={() =>
                                this.removeReason(index, "consumer")
                              }
                            >
                              <DeleteIcon
                                color="#343330"
                                width={20}
                                height={20}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {customConsumerCount < 5 && (
                      <button
                        type="button"
                        className="add-reason-button"
                        onClick={() => this.addReason("consumer")}
                      >
                        {customConsumerCount > 0
                          ? "+ Add More Reasons"
                          : "+ Add Reason"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            <CheckboxGroup
              label={getMessage("order.config.orderTypes")}
              name="orderTypes"
              options={[
                {
                  text: getMessage("offer.ordertype.options.delivery"),
                  value: "DELIVERY",
                },
                {
                  text: getMessage("offer.ordertype.options.pickup"),
                  value: "PICKUP",
                },
              ]}
              {...this.generateStateMappers({
                stateKeys: ["orderTypes"],
                loseEmphasisOnFill: true,
              })}
              required
            />
            <div className="payment-modes">
              <div className="order-by-payment-rules">
                <div className="payment-order-label">
                  {getMessage("choose.payment.mode.for.order.type")}
                </div>
                <table className="payment-mode-table">
                  <thead>
                    <tr>
                      <th className="description">
                        {getMessage("order.by.label.for.payment")}
                      </th>
                      {Array.isArray(availablePayments) &&
                        availablePayments.map((item) => (
                          <th className="description" key={item}>
                            {getMessage(
                              `operations.store.form.payment.${item}`
                            )}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {this.state.values?.orderTypes?.includes(DELIVERY) && (
                      <tr key={"DELIVERY"}>
                        <td className="order-type">
                          {getMessage("order.details.location.option.delivery")}
                        </td>
                        {Array.isArray(availablePayments) &&
                          availablePayments.map((item) => {
                            const hasConfigured =
                              this.state.values?.configuredPaymentsDelivery?.includes(
                                item
                              );
                            return (
                              <td
                                className="payments-list"
                                key={`${"DELIVERY"}-${item}`}
                              >
                                <Checkbox
                                  className="payment-mode"
                                  name={`${"DELIVERY"}-${item}`}
                                  value={hasConfigured}
                                  onChange={(e) =>
                                    this.onPaymentChange(e, "DELIVERY", item)
                                  }
                                />
                              </td>
                            );
                          })}
                      </tr>
                    )}
                    {this.state.values?.orderTypes?.includes(PICKUP) && (
                      <tr key={"PICKUP"}>
                        <td className="order-type">
                          {getMessage("order.details.location.option.pickup")}
                        </td>
                        {Array.isArray(availablePayments) &&
                          availablePayments.map((item) => {
                            const hasConfigured =
                              this.state.values?.configuredPaymentsPickup?.includes(
                                item
                              );
                            return (
                              <td
                                className="payments-list"
                                key={`${"PICKUP"}-${item}`}
                              >
                                <Checkbox
                                  className="payment-mode"
                                  name={`${"PICKUP"}-${item}`}
                                  value={hasConfigured}
                                  onChange={(e) =>
                                    this.onPaymentChange(e, "PICKUP", item)
                                  }
                                />
                              </td>
                            );
                          })}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="form-buttons-container">
              <SubmitButton disabled={this.state.submitting}>
                {getMessage("basicInfo.submit.text")}
              </SubmitButton>
              <button
                className="button"
                type="button"
                onClick={this.handleClose}
                disabled={this.state.submitting}
              >
                {getMessage("basicInfo.cancel.text")}
              </button>
            </div>
          </Form>
        )}
      </div>
    );
  }
}

export default withRouter(OrderManagementForm);
